import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Modal } from '../components/common/Modal';

interface ChemistryPair {
  playerAId: string;
  playerAName: string;
  playerBId: string;
  playerBName: string;
  chemistryScore: number;
  manualAdjustment: number;
  effectiveScore: number;
  matchesTogether: number;
  winsTogether: number;
  goalsFor: number;
  goalsAgainst: number;
  lastPlayedTogether: string | null;
}

interface Partnership {
  playerAName: string;
  playerBName: string;
  effectiveScore: number;
  matchesTogether: number;
  winsTogether: number;
  winRate: number;
  goalsFor: number;
  goalsAgainst: number;
}

interface ChemistryInsights {
  topPartnerships: Partnership[];
  bottomPartnerships: Partnership[];
  statistics: {
    totalPartnerships: number;
    avgChemistry: number;
    distribution: {
      excellent: number;
      good: number;
      fair: number;
      poor: number;
    };
  };
}

interface PlayerSimple {
  id: string;
  name: string;
}

const Chemistry: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState<ChemistryInsights | null>(null);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [selectedPair, setSelectedPair] = useState<Partnership | null>(null);
  const [adjustmentValue, setAdjustmentValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Player comparison state
  const [allPlayers, setAllPlayers] = useState<PlayerSimple[]>([]);
  const [chemistryMatrix, setChemistryMatrix] = useState<ChemistryPair[]>([]);
  const [selectedPlayerAId, setSelectedPlayerAId] = useState<string>('');
  const [selectedPlayerBId, setSelectedPlayerBId] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [insightsResponse, matrixResponse] = await Promise.all([
        apiClient.getChemistryInsights(),
        apiClient.getChemistryMatrix()
      ]);

      if (insightsResponse) {
        setInsights(insightsResponse);
      }

      if (matrixResponse) {
        setAllPlayers(matrixResponse.players);
        setChemistryMatrix(matrixResponse.matrix);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load chemistry data');
    } finally {
      setLoading(false);
    }
  };

  const loadChemistryData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getChemistryInsights();
      if (response) {
        setInsights(response);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load chemistry data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustChemistry = async () => {
    if (!selectedPair) return;

    try {
      // Need to get player IDs from the pair
      // For now, we'll need to enhance this
      setSuccessMessage('Chemistry adjustment saved');
      setShowAdjustModal(false);
      await loadChemistryData();
    } catch (err: any) {
      setError(err.message || 'Failed to adjust chemistry');
    }
  };

  const handleResetChemistry = async () => {
    if (!window.confirm('Are you sure you want to reset all chemistry data? This cannot be undone.')) {
      return;
    }

    try {
      await apiClient.resetChemistry();
      setSuccessMessage('Chemistry data reset successfully');
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to reset chemistry');
    }
  };

  const getChemistryColor = (score: number): string => {
    if (score >= 7.5) return 'text-green-600 dark:text-green-400';
    if (score >= 6) return 'text-blue-600 dark:text-blue-400';
    if (score >= 4) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getChemistryLabel = (score: number): string => {
    if (score >= 7.5) return 'Excellent';
    if (score >= 6) return 'Good';
    if (score >= 4) return 'Fair';
    return 'Poor';
  };

  const getChemistryBgColor = (score: number): string => {
    if (score >= 7.5) return 'bg-green-100 dark:bg-green-900/30';
    if (score >= 6) return 'bg-blue-100 dark:bg-blue-900/30';
    if (score >= 4) return 'bg-yellow-100 dark:bg-yellow-900/30';
    return 'bg-red-100 dark:bg-red-900/30';
  };

  const getComparisonData = (): ChemistryPair | null => {
    if (!selectedPlayerAId || !selectedPlayerBId || selectedPlayerAId === selectedPlayerBId) {
      return null;
    }

    // Find chemistry data for this pair (order doesn't matter)
    return chemistryMatrix.find(pair =>
      (pair.playerAId === selectedPlayerAId && pair.playerBId === selectedPlayerBId) ||
      (pair.playerAId === selectedPlayerBId && pair.playerBId === selectedPlayerAId)
    ) || null;
  };

  const clearComparison = () => {
    setSelectedPlayerAId('');
    setSelectedPlayerBId('');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  };

  if (!insights) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                No Chemistry Data Yet
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Chemistry data will be generated automatically as you save matches with scores.
                Play at least 3 matches with the same players to see chemistry insights.
              </p>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  const comparisonData = getComparisonData();

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Player Chemistry
              <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                New
              </span>
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Track player partnerships and performance based on match outcomes
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={handleResetChemistry}
            className="text-red-600 hover:text-red-700 dark:text-red-400 w-full sm:w-auto"
          >
            Reset All Data
          </Button>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-600 rounded-lg">
            <p className="text-green-800 dark:text-green-200">{successMessage}</p>
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-600 rounded-lg">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Player Comparison Section */}
        <Card className="mb-8">
          <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            🔍 Compare Players
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Select two players to see their chemistry rating, win rate, and goal difference
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Player 1
              </label>
              <select
                value={selectedPlayerAId}
                onChange={(e) => setSelectedPlayerAId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a player...</option>
                {allPlayers
                  .filter(p => p.id !== selectedPlayerBId)
                  .map(player => (
                    <option key={player.id} value={player.id}>
                      {player.name}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Player 2
              </label>
              <select
                value={selectedPlayerBId}
                onChange={(e) => setSelectedPlayerBId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a player...</option>
                {allPlayers
                  .filter(p => p.id !== selectedPlayerAId)
                  .map(player => (
                    <option key={player.id} value={player.id}>
                      {player.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {comparisonData && (
            <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {comparisonData.playerAName} & {comparisonData.playerBName}
                  </h3>
                </div>
                <Button
                  variant="ghost"
                  onClick={clearComparison}
                  className="text-sm"
                >
                  Clear
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold">
                    <span className={getChemistryColor(comparisonData.effectiveScore)}>
                      {comparisonData.effectiveScore.toFixed(1)}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Chemistry ({getChemistryLabel(comparisonData.effectiveScore)})
                  </p>
                </div>

                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {comparisonData.matchesTogether > 0
                      ? ((comparisonData.winsTogether / comparisonData.matchesTogether) * 100).toFixed(0)
                      : 0}%
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Win Rate
                  </p>
                </div>

                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {comparisonData.goalsFor - comparisonData.goalsAgainst > 0 ? '+' : ''}
                    {comparisonData.goalsFor - comparisonData.goalsAgainst}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Goal Difference
                  </p>
                </div>

                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {comparisonData.matchesTogether}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Matches Together
                  </p>
                </div>
              </div>
            </div>
          )}

          {selectedPlayerAId && selectedPlayerBId && !comparisonData && selectedPlayerAId !== selectedPlayerBId && (
            <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <p className="text-yellow-800 dark:text-yellow-200 text-center">
                No chemistry data available for this pair yet. They need to play at least 3 matches together.
              </p>
            </div>
          )}
        </Card>

        {/* Statistics Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-blue-600 dark:text-blue-400">
                {insights.statistics.totalPartnerships}
              </p>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                Total Partnerships
              </p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-green-600 dark:text-green-400">
                {insights.statistics.avgChemistry.toFixed(1)}
              </p>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                Average Chemistry
              </p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-green-600 dark:text-green-400">
                {insights.statistics.distribution.excellent}
              </p>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                Excellent (7.5+)
              </p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-red-600 dark:text-red-400">
                {insights.statistics.distribution.poor}
              </p>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                Poor (&lt;4.0)
              </p>
            </div>
          </Card>
        </div>

        {/* Top Partnerships */}
        <Card className="mb-8">
          <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            🏆 Top Partnerships
          </h2>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Players
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Chemistry
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Matches
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Win Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Goal Diff
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {insights.topPartnerships.map((partnership, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-3 md:px-6 py-3 md:py-4">
                      <div className="text-xs md:text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[120px]">
                        {partnership.playerAName} & {partnership.playerBName}
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium ${getChemistryBgColor(partnership.effectiveScore)}`}>
                        <span className={getChemistryColor(partnership.effectiveScore)}>
                          <span className="md:hidden">{partnership.effectiveScore.toFixed(1)}</span>
                          <span className="hidden md:inline">{partnership.effectiveScore.toFixed(1)} - {getChemistryLabel(partnership.effectiveScore)}</span>
                        </span>
                      </span>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900 dark:text-gray-100">
                      {partnership.matchesTogether}
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900 dark:text-gray-100">
                      {partnership.winRate.toFixed(0)}%
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900 dark:text-gray-100">
                      +{partnership.goalsFor - partnership.goalsAgainst}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Bottom Partnerships */}
        {insights.bottomPartnerships.length > 0 && (
          <Card>
            <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              ⚠️ Challenging Partnerships
            </h2>
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Players
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Chemistry
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                      Matches
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                      Win Rate
                    </th>
                    <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                      Goal Diff
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {insights.bottomPartnerships.map((partnership, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-3 md:px-6 py-3 md:py-4">
                        <div className="text-xs md:text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[120px]">
                          {partnership.playerAName} & {partnership.playerBName}
                        </div>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium ${getChemistryBgColor(partnership.effectiveScore)}`}>
                          <span className={getChemistryColor(partnership.effectiveScore)}>
                            <span className="md:hidden">{partnership.effectiveScore.toFixed(1)}</span>
                            <span className="hidden md:inline">{partnership.effectiveScore.toFixed(1)} - {getChemistryLabel(partnership.effectiveScore)}</span>
                          </span>
                        </span>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900 dark:text-gray-100">
                        {partnership.matchesTogether}
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900 dark:text-gray-100">
                        {partnership.winRate.toFixed(0)}%
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900 dark:text-gray-100">
                        {partnership.goalsFor - partnership.goalsAgainst > 0 ? '+' : ''}
                        {partnership.goalsFor - partnership.goalsAgainst}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Info Card */}
        <Card className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
            How Chemistry Works
          </h3>
          <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
            <li>• Chemistry scores range from 1-10 (higher is better)</li>
            <li>• Partnerships improve when teams win matches together</li>
            <li>• Blowout losses decrease chemistry between losing players</li>
            <li>• Requires at least 3 matches together for reliable data</li>
            <li>• Algorithm V3 uses chemistry to create better-balanced teams</li>
          </ul>
        </Card>
      </div>

      {/* Adjustment Modal */}
      <Modal
        isOpen={showAdjustModal}
        onClose={() => setShowAdjustModal(false)}
        title="Adjust Chemistry"
      >
        <div className="space-y-4">
          {selectedPair && (
            <>
              <p className="text-gray-700 dark:text-gray-300">
                Adjust chemistry between {selectedPair.playerAName} and {selectedPair.playerBName}
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Adjustment (-5 to +5)
                </label>
                <input
                  type="range"
                  min="-5"
                  max="5"
                  step="0.5"
                  value={adjustmentValue}
                  onChange={(e) => setAdjustmentValue(parseFloat(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mt-1">
                  <span>-5</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {adjustmentValue > 0 ? '+' : ''}{adjustmentValue}
                  </span>
                  <span>+5</span>
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <Button variant="ghost" onClick={() => setShowAdjustModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAdjustChemistry}>
                  Save Adjustment
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </Layout>
  );
};

export default Chemistry;
